/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';

import {useContextIndependentPage} from "../../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../../Hooks/StyleHooks";

import {StreamBankPageHeader} from '../../../Common/StreamBankPageHeader';
import {SpeciesTable} from "../Common/SpeciesTable/SpeciesTable";
import {SpeciesTableFilter} from "../Common/SpeciesTable/SpeciesTableFilter";
import {SpeciesList, SpeciesListSpecies} from "../../../../Models";
import {service as SpeciesListService} from '../../../../Services/SpeciesListService';
import {service as SpeciesListSpeciesService} from '../../../../Services/SpeciesListSpeciesService';
import {service as SpeciesService} from '../../../../Services/SpeciesService';
import {DocumentUtils, ObjectUtils} from "../../../../Utils";
import {SpeciesListSpeciesFields} from "../../../Common/Species/SpeciesListSpeciesFields";
import {useMakePageRequest} from "../../../../Hooks/RequestHooks";
import {usePermission} from "../../../../Hooks/PermissionHooks";
import {ConfirmationDialog} from "../../../Common/ConfirmationDialog";
import {GenericModelViewer} from "../../../Common/GenericModelViewer";

import {
    commonName,
    growthForm,
    invasivity,
    nativity,
    scientificName,
    visibility
} from "../Common/SpeciesTable/ColumnDefinitions";
import {EditSpeciesModal} from "../Common/Modals/EditSpeciesModal";
import {AddSpeciesModal} from "../Common/Modals/AddSpeciesModal";
import {SpeciesEmptyMessageFooter} from "../Common/SpeciesTable/SpeciesEmptyMessageFooter";

const model = new SpeciesList();
const childModel = new SpeciesListSpecies();

export const CustomListViewPage = props => {
    useContextIndependentPage(props);

    const classNames = useClassNames('sb-species-list-view-page');
    const makePageRequest = useMakePageRequest();
    const permission = usePermission();

    const currentUser = useSelector(state => state.userState.user);

    const [species, setSpecies] = useState(null);
    const [mergedSpecies, setMergedSpecies] = useState(null);
    const [speciesList, setSpeciesList] = useState(null);
    const [speciesListSpecies, setSpeciesListSpecies] = useState([]);
    const [editingSpeciesListSpecies, setEditingSpeciesListSpecies] = useState(null);
    const [speciesListValidation, setSpeciesListValidation] = useState([]);
    const [speciesListSpeciesValidation, setSpeciesListSpeciesValidation] = useState([]);
    const [speciesListSpeciesServerError, setSpeciesListSpeciesServerError] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [saveDialog, setSaveDialog] = useState(false);
    const [speciesTableFilters, setSpeciesTableFilters] = useState([]);
    const [editingSpecies, setEditingSpecies] = useState(false);

    const speciesListId = props?.match?.params?.speciesListId;

    useEffect(() => {
        loadData();
    }, [speciesListId]);

    useEffect(() => {
        if(species && speciesListSpecies) {
            const newMergedSpecies = species.map(record => ({
                ...record,
                SpeciesListSpecies: speciesListSpecies?.find?.(x => x.speciesId === record.speciesId),
            }));
            setMergedSpecies(newMergedSpecies);
        } else {
            setMergedSpecies(null)
        }
    }, [JSON.stringify(species), JSON.stringify(speciesListSpecies)]);

    const loadSpecies = async () => {
        const species = (await SpeciesService.Get())?.result ?? [];
        setSpecies(species);
    }

    const loadSpeciesList = async (speciesListId) => {
        const speciesList = (await SpeciesListService.GetSpeciesList(speciesListId))?.result ?? {};
        setSpeciesList(speciesList);
    }

    const loadSpeciesListsSpecies = async (speciesListId) => {
        const speciesListSpecies = (await SpeciesListSpeciesService.GetSpeciesListSpeciesByList(speciesListId))?.result ?? [];
        setSpeciesListSpecies(speciesListSpecies);
    }

    const loadData = () => {
        const promises = [loadSpecies()];
        if(speciesListId === "new") {
            setSpeciesList({
                organizationId: currentUser?.organizationId,
                speciesListTypeId: 4,
                markAsReviewed: false,
            });
            setSpeciesListSpecies([]);

        } else {
            promises.concat([
                void loadSpeciesList(speciesListId),
                void loadSpeciesListsSpecies(speciesListId),
            ]);
        }
        makePageRequest(promises);
    };

    const updateSpeciesList = (speciesList) => {
        setSpeciesList(speciesList);
    };

    const saveSpeciesList = () => {
        if(speciesList.speciesListId) {
            makePageRequest(
                _ => SpeciesListService.UpdateSpeciesList(speciesList),
                x => {
                    setSpeciesList(x.result);
                    setSaveDialog(true);
                }
            );
        } else {
            makePageRequest(
                _ => SpeciesListService.CreateSpeciesList(speciesList),
                x => {
                    setSaveDialog(true);
                    DocumentUtils.changePage("/speciesLists/" + x?.result?.speciesListId);
                }
            );
        }
    };

    const deleteSpeciesList = () => {
        makePageRequest(
            _ => SpeciesListService.DeleteSpeciesList(speciesList.speciesListId),
            _ => DocumentUtils.changePage("/speciesLists")
        );
    };

    const updateValidation = (validation, _) => {
        setSpeciesListValidation(ObjectUtils.Clone(validation));
    };

    const isValid = () => {
        for(const propertyName in speciesListValidation) {
            const validation = speciesListValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    const isChildValid = () => {
        for(let propertyName in speciesListSpeciesValidation) {
            let validation = speciesListSpeciesValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    const getOriginalValue = (speciesListSpecies, key) => {
        const species = mergedSpecies?.find?.(x => x.speciesId === speciesListSpecies?.speciesId);
        const originalValueKey = SpeciesListSpeciesFields?.[key] ?? key;
        return species?.[originalValueKey] ?? null;
    };

    const cleanSpeciesListSpecies = (speciesListSpecies) => {
        let target = ObjectUtils.Clone(speciesListSpecies);

        if (target) {
            Object.keys(SpeciesListSpeciesFields)?.forEach(key => {
                const originalValue = getOriginalValue?.(target, key);

                if (target[key] === originalValue) {
                    target[key] = null;
                }
            });
        }

        return target;
    };

    const saveSpeciesListSpecies = () => {
        const cleanedSpeciesListSpecies = cleanSpeciesListSpecies?.(editingSpeciesListSpecies);

        if(cleanedSpeciesListSpecies) {
            setSpeciesListSpeciesServerError(null);
            makePageRequest(
                _ => cleanedSpeciesListSpecies?.speciesListSpeciesId ?
                    SpeciesListSpeciesService?.UpdateSpeciesListSpecies?.(cleanedSpeciesListSpecies):
                    SpeciesListSpeciesService?.CreateSpeciesListSpecies?.(cleanedSpeciesListSpecies),
                _ => {
                    loadData();
                    setEditingSpeciesListSpecies(null);
                },
                response => {
                    setSpeciesListSpeciesServerError(response?.message);
                }
            );
        }
    };

    const exitEditMode = () => {
        DocumentUtils.changePage("/speciesLists");
    };

    const openSpeciesListSpeciesEditor = (id) => {
        const target = speciesListSpecies?.find(x => x.speciesId === id);
        const baseSpecies = species?.find(x => x.speciesId === id);
        if(target) {
            setEditingSpeciesListSpecies({...target});
        } else {
            setEditingSpeciesListSpecies({
                speciesId: id,
                speciesListId: speciesListId,
                alternativeLatinName: null,
                alternativeCommonName: null,
                speciesTypeId: baseSpecies?.speciesTypeId,
                speciesInvasivityId: baseSpecies?.speciesInvasivityId,
                speciesNativityId: baseSpecies?.speciesNativityId,
                searchable: 1
            });
        }
    };

    const addDefaultSpeciesListSpecies = () => {
        SpeciesListSpeciesService.CreateDefaults(speciesListId)
            .then((speciesListSpecies) => {
                setSpeciesListSpecies(speciesListSpecies?.result || []);
            });
    };

    const deleteSpecies = (id) => {
        void makePageRequest?.(
            _ => SpeciesListSpeciesService.DeleteSpeciesListSpecies(id),
            () => loadSpeciesListsSpecies(speciesListId),
        );
    };

    const quickAdd = (id) => {
        const request = {
            speciesId: id,
            speciesListId: speciesListId,
            searchable: 1
        };

        void makePageRequest?.(
            _ => SpeciesListSpeciesService?.CreateSpeciesListSpecies?.(request),
            () => loadSpeciesListsSpecies(speciesListId),
        );
    };

    const isNew = speciesListId === "new";

    // don't render page before it is loaded
    if (speciesList === null) {
        return null;
    }

    // permissions
    const canEditGlobalList = permission?.('can_alter_global_species_list');
    const canDelete = speciesList?.speciesListId && permission('can_delete_species_list');
    const skipsDataSegmentation = permission?.("skips_data_segmentation");
    const canEditSpeciesLists = permission?.('can_update_species_list');
    const canEditThisOrganization = skipsDataSegmentation || speciesList.organizationId === currentUser?.organizationId;
    const canEditThisListType = canEditGlobalList || (Number(speciesList.speciesListTypeId) === 4);
    const canEditThisSpeciesList = canEditSpeciesLists && canEditThisOrganization && canEditThisListType;

    const columnDefinitions = [
        scientificName,
        commonName,
        growthForm,
        nativity,
        invasivity,
        visibility,
    ];

    const tableHasFilters = !!(speciesTableFilters?.filter(x => x.code !== 'showAll')?.length);

    return <Container className={classNames}>
        <StreamBankPageHeader
            Title={"Plant Species List: " + (speciesList?.name || "New Species List")}
        />

        <ConfirmationDialog
            Title="Confirmation"
            Body={"Are you sure you want to delete this Species List?"}
            Display={deleteDialog}
            OnClose={() => setDeleteDialog(false)}
            OnConfirm={() => deleteSpeciesList()}
        />

        <ConfirmationDialog
            Title="Saved"
            Body={"The species list was saved successfully."}
            Display={saveDialog}
            OnConfirm={() =>setSaveDialog(false)}
            ConfirmationButtonMessage={"OK"}
        />

        {
            !!editingSpeciesListSpecies &&
            <ConfirmationDialog
                size="lg"
                Title={editingSpeciesListSpecies?.speciesListSpeciesId == null ? "Add Species": "Edit Species"}
                Body={[
                    (speciesListSpeciesServerError) &&
                    <Row key={1}>
                        <Col xs={12}>
                            <p className="sb-model-viewer-validation-failure-text">{speciesListSpeciesServerError}</p>
                        </Col>
                    </Row>,
                    <Row key={2}>
                        <Col xs={12}>
                            <GenericModelViewer
                                IsNewModel={editingSpeciesListSpecies?.speciesListSpeciesId == null}
                                Model={childModel}
                                EditMode={true}
                                Data={editingSpeciesListSpecies}
                                Update={(x) => setEditingSpeciesListSpecies(x)}
                                OnValidationUpdate={(v, _) => setSpeciesListSpeciesValidation(v)}
                                HideGroups={[]}
                                CustomData={{
                                    SpeciesList: speciesList,
                                    Species: mergedSpecies?.find?.(x => x.speciesId === editingSpeciesListSpecies?.speciesId)
                                }}
                            />
                        </Col>
                    </Row>
                ]}
                Display={editingSpeciesListSpecies != null}
                OnHide={() => setEditingSpeciesListSpecies(null)}
                OnClose={() => setEditingSpeciesListSpecies(null)}
                CancelButtonMessage={"Cancel"}
                DisableConfirmationButton={!isChildValid()}
                OnConfirm={saveSpeciesListSpecies}
                ConfirmationButtonMessage={editingSpeciesListSpecies?.speciesListSpeciesId == null ? "Add Species": "Save"}
            />
        }

        <Row>
            <Col xs={12}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            canEditThisSpeciesList ?
                                <Button
                                    variant="save"
                                    onClick={saveSpeciesList}
                                    disabled={!isValid()}
                                >
                                    Save
                                </Button>:
                                null
                        }
                        <Button
                            variant="cancel"
                            onClick={() => exitEditMode()}
                        >
                            Cancel
                        </Button>
                        {
                            canDelete && canEditThisSpeciesList ?
                                <Button
                                    variant="delete"
                                    onClick={() => setDeleteDialog(true)}
                                >
                                    Delete
                                </Button>:
                                null
                        }
                    </div>
                    Plant Species List: {speciesList?.name || "Species List"}
                </h2>
            </Col>
        </Row>
        <Row>
            <Col xs={12} md={6}>
                <GenericModelViewer
                    IsNewModel={isNew}
                    DefaultSelectedFieldDefKey={isNew ? "name": null}
                    Model={model}
                    EditMode={canEditThisSpeciesList}
                    Data={speciesList}
                    Update={updateSpeciesList}
                    OnValidationUpdate={updateValidation}
                    HideGroups={['Notes']}
                    CustomData={{}}
                />
            </Col>
            <Col xs={12} md={6}>
                <GenericModelViewer
                    IsNewModel={isNew}
                    DefaultSelectedFieldDefKey={isNew ? "name": null}
                    Model={model}
                    EditMode={canEditThisSpeciesList}
                    Data={speciesList}
                    Update={updateSpeciesList}
                    OnValidationUpdate={updateValidation}
                    HideGroups={['General', 'History']}
                    CustomData={{}}
                />
            </Col>
            <Col xs={12} md={6} className="d-block d-md-none" />
        </Row>
        {
            speciesListId !== "new" ?
                <Fragment>
                    <Row>
                        <Col xs={12}>
                            <h2 className="atomic-sectionheader">
                                Plant Species
                            </h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <SpeciesTable
                                SpeciesListId={speciesList?.speciesListId}
                                FilterComponent={SpeciesTableFilter}
                                OnFilterUpdate={setSpeciesTableFilters}
                                Data={mergedSpecies}
                                OnEditClick={openSpeciesListSpeciesEditor}
                                OnDeleteClick={deleteSpecies}
                                OnQuickAdd={quickAdd}
                                emptyMessage={
                                    !tableHasFilters &&
                                    <Fragment>
                                        <div>This custom list has no species.</div>
                                        <br/>
                                        <button
                                            className="m-auto d-block btn btn-primary"
                                            onClick={addDefaultSpeciesListSpecies}
                                        >
                                            Add All Default Species
                                        </button>
                                    </Fragment>
                                }
                                emptyMessageFooter={
                                    tableHasFilters &&
                                    <SpeciesEmptyMessageFooter onReload={loadData} />
                                }
                                columnDefinitions={columnDefinitions}
                                showGlobalIcon={true}
                            />
                        </Col>
                    </Row>
                </Fragment>:
                null
        }
    </Container>;
};
